// 文章数据管理工具
import staticData from '../data/articles_static.json';

// 自动根据标题生成封面图标
export const generateCoverIcon = (title) => {
  if (!title) return '📖';
  
  const lowerTitle = title.toLowerCase();
  
  // 科技相关
  if (/robot|ai|tech|computer|artificial|data|digital|cyber|software|app|internet/i.test(lowerTitle)) {
    return '🤖';
  }
  
  // 体育/足球相关
  if (/sport|soccer|football|basketball|tennis|game|match|team|player|world cup|olympic/i.test(lowerTitle)) {
    return '⚽';
  }
  
  // 文学/书籍相关
  if (/book|novel|literature|read|author|story|poem|fiction|essay/i.test(lowerTitle)) {
    return '📚';
  }
  
  // 商业/经济相关
  if (/business|econom|market|trade|finance|stock|company|startup|entrepreneur/i.test(lowerTitle)) {
    return '💼';
  }
  
  // 科学相关
  if (/science|research|study|discover|invent|experiment|lab|scientist/i.test(lowerTitle)) {
    return '🔬';
  }
  
  // 健康/医疗相关
  if (/health|medical|doctor|hospital|disease|virus|vaccine|patient|medicine/i.test(lowerTitle)) {
    return '🏥';
  }
  
  // 环境/气候相关
  if (/climate|environment|climate|green|eco|planet|earth|nature|animal|plant/i.test(lowerTitle)) {
    return '🌍';
  }
  
  // 政治/国际关系相关
  if (/politics|government|election|war|peace|diplomat|country|nation|president|policy/i.test(lowerTitle)) {
    return '🏛️';
  }
  
  // 教育相关
  if (/education|school|university|student|teacher|learn|study|college|academy/i.test(lowerTitle)) {
    return '🎓';
  }
  
  // 娱乐/电影/音乐相关
  if (/movie|film|music|song|artist|concert|celebrity|star|entertainment|hollywood/i.test(lowerTitle)) {
    return '🎬';
  }
  
  // 默认图标
  return '📖';
};

// 获取所有文章列表
export const getAllArticles = () => {
  const articles = localStorage.getItem('articles');
  const dataVersion = localStorage.getItem('data_version');
  
  // 如果有本地数据且版本匹配，返回本地数据
  if (articles && dataVersion === 'v20260610_4articles') {
    return JSON.parse(articles);
  }
  
  // 首次加载或版本不匹配，使用静态数据
  localStorage.setItem('articles', JSON.stringify(staticData.articles));
  localStorage.setItem('data_version', 'v20260610_4articles');
  return staticData.articles;
};

// 保存文章列表
export const saveArticles = (articles) => {
  localStorage.setItem('articles', JSON.stringify(articles));
};

// 添加单篇文章
export const addArticle = (articleData) => {
  const articles = getAllArticles();
  const newId = articles.length > 0 ? Math.max(...articles.map(a => a.id)) + 1 : 1;
  const newArticle = {
    id: newId,
    title: articleData.title || '未命名文章',
    source: articleData.source || '未知来源',
    cover: articleData.cover || generateCoverIcon(articleData.title), // 自动生成图标
    date: articleData.date || new Date().toISOString().split('T')[0],
    enabled: true,
    audioFile: articleData.audioFile || null
  };
  articles.push(newArticle);
  saveArticles(articles);
  
  // 保存文章详情
  saveArticleDetail(newId, articleData);
  return newArticle;
};

// 批量导入文章
export const importArticles = (jsonData) => {
  try {
    const articles = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
    
    if (!Array.isArray(articles)) {
      throw new Error('JSON格式错误：必须是数组');
    }
    
    const currentArticles = getAllArticles();
    let importedCount = 0;
    
    articles.forEach(articleData => {
      if (articleData.title && articleData.paragraphs && Array.isArray(articleData.paragraphs)) {
        const existingArticle = currentArticles.find(a => a.title === articleData.title);
        if (!existingArticle) {
          const newId = currentArticles.length > 0 ? Math.max(...currentArticles.map(a => a.id)) + 1 : 1;
          const newArticle = {
            id: newId,
            title: articleData.title,
            source: articleData.source || 'AI Generated',
            cover: articleData.cover || generateCoverIcon(articleData.title), // 自动生成图标
            date: articleData.date || new Date().toISOString().split('T')[0],
            enabled: true,
            audioFile: articleData.audioFile || null
          };
          currentArticles.push(newArticle);
          saveArticleDetail(newId, articleData);
          importedCount++;
        }
      }
    });
    
    saveArticles(currentArticles);
    return { success: true, count: importedCount, message: `成功导入 ${importedCount} 篇文章` };
  } catch (error) {
    return { success: false, count: 0, message: `导入失败：${error.message}` };
  }
};

// 获取文章详情
export const getArticleDetail = (articleId) => {
  const detail = localStorage.getItem(`article_${articleId}`);
  if (detail) {
    return JSON.parse(detail);
  }
  // 如果本地没有，尝试从静态文件加载
  if (staticData.details[articleId]) {
    return staticData.details[articleId];
  }
  return null;
};

// 保存文章详情
export const saveArticleDetail = (articleId, detail) => {
  localStorage.setItem(`article_${articleId}`, JSON.stringify(detail));
};

// 删除文章
export const deleteArticle = (articleId) => {
  const articles = getAllArticles().filter(a => a.id !== articleId);
  saveArticles(articles);
  localStorage.removeItem(`article_${articleId}`);
};

// 切换文章状态
export const toggleArticleStatus = (articleId) => {
  const articles = getAllArticles();
  const article = articles.find(a => a.id === articleId);
  if (article) {
    article.enabled = !article.enabled;
    saveArticles(articles);
  }
};

// 生成导入模板JSON
export const generateImportTemplate = () => {
  return JSON.stringify([
    {
      "title": "文章标题示例",
      "source": "文章来源（如：The Economist）",
      "summary": "文章摘要（英文，不超过60词）",
      "cover": "🤖",  // 可留空，系统会根据标题自动生成
      "date": "2026-06-09",
      "audioFile": "article_2_full.m4a",
      "paragraphs": [
        {
          "original": "英文原文段落，<strong>重点词汇</strong>用strong标签标记",
          "translation": "对应的中文翻译",
          "vocab": [
            {
              "word": "vocabulary",
              "pos": "n.",
              "meaning_cn": "词汇",
              "meaning_en": "words used in a particular language",
              "level": "L2"
            }
          ],
          "audioStart": 0,
          "audioEnd": 15
        }
      ],
      "questions": [
        {
          "question": "阅读理解题目示例：根据文章内容，以下哪个选项正确？",
          "options": [
            {"key": "A", "text": "选项A的内容"},
            {"key": "B", "text": "选项B的内容"},
            {"key": "C", "text": "选项C的内容"},
            {"key": "D", "text": "选项D的内容"}
          ],
          "correctAnswer": "C",
          "analysis": "本题考查细节理解。根据文章第X段的内容，可以推断出正确答案为C。"
        }
      ]
    }
  ], null, 2);
};

// 获取文章音频文件
export const getArticleAudioFile = (articleId) => {
  const article = getAllArticles().find(a => a.id === articleId);
  if (article && article.audioFile) {
    return `/audio/${article.audioFile}`;
  }
  return null;
};

// 更新文章音频文件
export const updateArticleAudio = (articleId, audioFileName) => {
  const articles = getAllArticles();
  const article = articles.find(a => a.id === articleId);
  if (article) {
    article.audioFile = audioFileName;
    saveArticles(articles);
    
    // 更新详情中的音频信息
    const detail = getArticleDetail(articleId);
    if (detail) {
      detail.audioFile = audioFileName;
      saveArticleDetail(articleId, detail);
    }
    return true;
  }
  return false;
};

// 更新文章详情（包括段落时间戳）
export const updateArticleDetail = (articleId, updatedDetail) => {
  const existingDetail = getArticleDetail(articleId);
  if (existingDetail) {
    const newDetail = { ...existingDetail, ...updatedDetail };
    saveArticleDetail(articleId, newDetail);
    return true;
  }
  return false;
};

// 更新单个段落的时间戳
export const updateParagraphTimestamps = (articleId, paragraphIndex, audioStart, audioEnd) => {
  const detail = getArticleDetail(articleId);
  if (detail && detail.paragraphs && detail.paragraphs[paragraphIndex]) {
    detail.paragraphs[paragraphIndex].audioStart = audioStart;
    detail.paragraphs[paragraphIndex].audioEnd = audioEnd;
    saveArticleDetail(articleId, detail);
    
    // 同步更新列表中的音频文件信息（如果有）
    const articles = getAllArticles();
    const article = articles.find(a => a.id === articleId);
    if (article && detail.audioFile) {
      article.audioFile = detail.audioFile;
      saveArticles(articles);
    }
    return true;
  }
  return false;
};